// ── Tool Registry — 所有预置工具的定义 ──

export const TOOL_CATEGORIES = [
  { id: "docking", label: "Docking", icon: "🔌" },
  { id: "md", label: "MD", icon: "⚡" },
  { id: "fep", label: "FEP", icon: "⚖️" },
  { id: "generation", label: "分子生成", icon: "🧬" },
  { id: "prediction", label: "性质预测", icon: "💊" },
  { id: "descriptors", label: "描述符", icon: "📐" },
  { id: "custom", label: "自定义", icon: "📜" },
];

/** 参数类型定义 */
const PARAM_TYPES = {
  STRING: "string",
  NUMBER: "number",
  BOOLEAN: "boolean",
  SELECT: "select",
  FILE: "file",
  RANGE: "range",
};

const tools = [
  // ── 1. AutoDock Vina ──
  {
    id: "docking",
    name: "AutoDock Vina",
    description: "基于经验力场的蛋白质-配体分子对接工具",
    icon: "🔌",
    category: "docking",
    color: "#3b82f6",
    parameters: [
      { key: "receptor", label: "Receptor (PDB)", type: PARAM_TYPES.FILE, required: true, accept: ".pdb,.pdbqt", description: "蛋白结构文件" },
      { key: "centerX", label: "Center X (Å)", type: PARAM_TYPES.NUMBER, defaultValue: 0, required: true, description: "对接盒子中心 X 坐标" },
      { key: "centerY", label: "Center Y (Å)", type: PARAM_TYPES.NUMBER, defaultValue: 0, required: true, description: "对接盒子中心 Y 坐标" },
      { key: "centerZ", label: "Center Z (Å)", type: PARAM_TYPES.NUMBER, defaultValue: 0, required: true, description: "对接盒子中心 Z 坐标" },
      { key: "boxSize", label: "Box Size (Å)", type: PARAM_TYPES.RANGE, defaultValue: 20, min: 10, max: 40, step: 2, description: "对接盒子大小" },
      { key: "exhaustiveness", label: "Exhaustiveness", type: PARAM_TYPES.RANGE, defaultValue: 8, min: 1, max: 16, step: 1, description: "搜索详尽程度" },
      { key: "numModes", label: "Num Modes", type: PARAM_TYPES.NUMBER, defaultValue: 9, min: 1, max: 20, description: "最大输出构象数" },
      { key: "energyRange", label: "Energy Range", type: PARAM_TYPES.NUMBER, defaultValue: 3, min: 1, max: 10, description: "能量范围 (kcal/mol)" },
    ],
    inputPorts: [
      { id: "in_molecules", label: "分子集", dataType: "molecule_set" },
      { id: "in_protein", label: "蛋白结构", dataType: "protein_structure" },
    ],
    outputPorts: [
      { id: "out_scores", label: "对接分数", dataType: "docking_score" },
    ],
    docs: "AutoDock Vina 是一款开源分子对接软件...",
  },

  // ── 2. GROMACS MD ──
  {
    id: "md",
    name: "GROMACS MD",
    description: "分子动力学模拟，分析蛋白-配体复合物稳定性",
    icon: "⚡",
    category: "md",
    color: "#8b5cf6",
    parameters: [
      { key: "forceField", label: "Force Field", type: PARAM_TYPES.SELECT, defaultValue: "amber99sb", required: true,
        options: [
          { label: "AMBER99SB-ILDN", value: "amber99sb" },
          { label: "CHARMM36", value: "charmm36" },
          { label: "GROMOS96 54a7", value: "gromos96" },
        ], description: "力场选择" },
      { key: "waterModel", label: "Water Model", type: PARAM_TYPES.SELECT, defaultValue: "tip3p",
        options: [
          { label: "TIP3P", value: "tip3p" },
          { label: "TIP4P", value: "tip4p" },
          { label: "SPC", value: "spc" },
        ], description: "水分子模型" },
      { key: "simTime", label: "Simulation Time (ns)", type: PARAM_TYPES.NUMBER, defaultValue: 100, min: 1, max: 1000, description: "模拟时长" },
      { key: "temperature", label: "Temperature (K)", type: PARAM_TYPES.NUMBER, defaultValue: 300, min: 200, max: 400, description: "模拟温度" },
      { key: "numRuns", label: "Replicas", type: PARAM_TYPES.NUMBER, defaultValue: 3, min: 1, max: 10, description: "平行重复次数" },
    ],
    inputPorts: [
      { id: "in_complex", label: "蛋白-配体复合物", dataType: "protein_structure" },
    ],
    outputPorts: [
      { id: "out_trajectory", label: "MD 轨迹", dataType: "md_trajectory" },
      { id: "out_energy", label: "能量数据", dataType: "property_table" },
    ],
    docs: "GROMACS 是一个高效的分子动力学模拟软件包...",
  },

  // ── 3. FEP Calculation ──
  {
    id: "fep",
    name: "FEP Calculation",
    description: "自由能微扰计算，预测相对结合自由能 ΔΔG",
    icon: "⚖️",
    category: "fep",
    color: "#ec4899",
    parameters: [
      { key: "engine", label: "Engine", type: PARAM_TYPES.SELECT, defaultValue: "openmm",
        options: [
          { label: "OpenMM", value: "openmm" },
          { label: "NAMD", value: "namd" },
          { label: "Desmond", value: "desmond" },
        ], description: "FEP 计算引擎" },
      { key: "lambdaWindows", label: "Lambda Windows", type: PARAM_TYPES.NUMBER, defaultValue: 12, min: 5, max: 30, description: "自由能窗数" },
      { key: "simTimePerWindow", label: "Time Per Window (ps)", type: PARAM_TYPES.NUMBER, defaultValue: 500, min: 100, max: 5000, description: "每窗模拟时长" },
      { key: "temperature", label: "Temperature (K)", type: PARAM_TYPES.NUMBER, defaultValue: 300, min: 200, max: 400 },
    ],
    inputPorts: [
      { id: "in_ligand_pair", label: "配体对", dataType: "molecule_set" },
      { id: "in_protein", label: "蛋白结构", dataType: "protein_structure" },
    ],
    outputPorts: [
      { id: "out_ddg", label: "ΔΔG 结果", dataType: "fep_result" },
    ],
    docs: "自由能微扰 (FEP) 是一种高精度计算结合自由能差异的方法...",
  },

  // ── 4. 分子生成 ──
  {
    id: "molecule-generation",
    name: "分子生成",
    description: "基于 AI 模型生成新型分子结构",
    icon: "🧬",
    category: "generation",
    color: "#22c55e",
    parameters: [
      { key: "model", label: "Generation Model", type: PARAM_TYPES.SELECT, defaultValue: "reinvent", required: true,
        options: [
          { label: "REINVENT", value: "reinvent" },
          { label: "MolGPT", value: "molgpt" },
          { label: "GraphGA", value: "graphga" },
        ], description: "分子生成模型" },
      { key: "numMolecules", label: "Number of Molecules", type: PARAM_TYPES.NUMBER, defaultValue: 1000, min: 10, max: 100000, description: "生成的分子数量" },
      { key: "scaffold", label: "Scaffold SMILES", type: PARAM_TYPES.STRING, required: false, placeholder: "C1=CC=CC=C1", description: "基于骨架生成（可选）" },
      { key: "temperature", label: "Temperature", type: PARAM_TYPES.RANGE, defaultValue: 1.0, min: 0.1, max: 2.0, step: 0.1, description: "采样温度" },
      { key: "propertyFilter", label: "Property Filter", type: PARAM_TYPES.SELECT, defaultValue: "none",
        options: [
          { label: "无", value: "none" },
          { label: "QED ≥ 0.5", value: "qed05" },
          { label: "QED ≥ 0.7", value: "qed07" },
          { label: "SA Score ≤ 4", value: "sa4" },
          { label: "LogP 1~5", value: "logp15" },
        ], description: "生成后的性质过滤" },
    ],
    inputPorts: [
      { id: "in_seed", label: "种子分子", dataType: "molecule_set" },
    ],
    outputPorts: [
      { id: "out_generated", label: "生成的分子", dataType: "generated_molecules" },
    ],
    docs: "分子生成利用深度学习模型从头设计具有期望性质的新分子...",
  },

  // ── 5. ADMET 预测 ──
  {
    id: "admet",
    name: "ADMET 预测",
    description: "吸收、分布、代谢、排泄、毒性预测（内部服务）",
    icon: "💊",
    category: "prediction",
    color: "#f97316",
    parameters: [
      { key: "endpoints", label: "Endpoints", type: PARAM_TYPES.SELECT, defaultValue: "all",
        options: [
          { label: "全部", value: "all" },
          { label: "吸收 (Absorption)", value: "absorption" },
          { label: "代谢 (Metabolism)", value: "metabolism" },
          { label: "毒性 (Toxicity)", value: "toxicity" },
          { label: "类药性 (Drug-likeness)", value: "druglikeness" },
        ], description: "预测端点选择" },
      { key: "modelVersion", label: "Model Version", type: PARAM_TYPES.SELECT, defaultValue: "v2",
        options: [
          { label: "v2 (最新)", value: "v2" },
          { label: "v1 (稳定版)", value: "v1" },
        ], description: "模型版本" },
    ],
    inputPorts: [
      { id: "in_molecules", label: "分子集", dataType: "molecule_set" },
    ],
    outputPorts: [
      { id: "out_properties", label: "ADMET 属性", dataType: "property_table" },
    ],
    docs: "内部 ADMET 预测服务...",
  },

  // ── 6. RDKit Descriptors ──
  {
    id: "rdkit",
    name: "RDKit Descriptors",
    description: "计算分子的物理化学描述符（MW、LogP、HBD、HBA 等）",
    icon: "📐",
    category: "descriptors",
    color: "#14b8a6",
    parameters: [
      { key: "descriptorSet", label: "Descriptor Set", type: PARAM_TYPES.SELECT, defaultValue: "all",
        options: [
          { label: "全部 (200+)", value: "all" },
          { label: "基础性质", value: "basic" },
          { label: "3D 描述符", value: "3d" },
          { label: "指纹 (Morgan)", value: "fingerprints" },
        ], description: "描述符集合" },
    ],
    inputPorts: [
      { id: "in_molecules", label: "分子集", dataType: "molecule_set" },
    ],
    outputPorts: [
      { id: "out_descriptors", label: "描述符表", dataType: "property_table" },
    ],
    docs: "RDKit 是一个开源化学信息学工具包...",
  },

  // ── 7. 自定义脚本 ──
  {
    id: "custom-script",
    name: "自定义脚本",
    description: "上传并运行自定义脚本（Python、R、Shell）",
    icon: "📜",
    category: "custom",
    color: "#64748b",
    parameters: [
      { key: "scriptFile", label: "Script File", type: PARAM_TYPES.FILE, required: true, accept: ".py,.r,.sh,.R", description: "上传脚本文件" },
      { key: "runtime", label: "Runtime", type: PARAM_TYPES.SELECT, defaultValue: "python3",
        options: [
          { label: "Python 3", value: "python3" },
          { label: "R", value: "r" },
          { label: "Bash", value: "bash" },
        ], description: "脚本运行时" },
      { key: "cmdArgs", label: "Command Arguments", type: PARAM_TYPES.STRING, required: false, placeholder: "--input {in} --output {out}", description: "命令行参数" },
    ],
    inputPorts: [
      { id: "in_data", label: "输入数据", dataType: "any" },
    ],
    outputPorts: [
      { id: "out_result", label: "输出结果", dataType: "any" },
    ],
    docs: "通过自定义脚本扩展 Screening 工作流的功能...",
  },
];

export default tools;

/** 根据工具 ID 获取工具定义 */
export function getToolById(toolId) {
  return tools.find((t) => t.id === toolId) || null;
}

/** 按分类分组工具 */
export function getToolsByCategory() {
  const grouped = {};
  tools.forEach((t) => {
    if (!grouped[t.category]) grouped[t.category] = [];
    grouped[t.category].push(t);
  });
  return grouped;
}
